import { isStagingTestMode, stagingEmailMessage } from '../lib/stagingEnvironment'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import SetupRecordForm, {
  SETUP_EMPTY,
  SetupRecordSummary,
  defaultSetupName,
  setupPayloadFromForm,
  setupTypeLabel,
  toSetupForm,
} from '../components/SetupRecordForm'
import { supabase } from '../lib/supabase'
import RestoredCustomerEvidence from '../components/RestoredCustomerEvidence'
import { clearPortalMagicIntent, rememberPortalMagicIntent, safePortalPath } from '../lib/portalAuth'
import {
  bookingPricingSummary,
  serviceBookingLabel,
  serviceCategoryLabel,
  serviceModuleKey,
  servicePriceLabel,
  servicePricingMode,
} from '../lib/serviceMenu'

const SERVICE_MODULE_ORDER = ['suspension', 'general']
const SERVICE_MODULE_LABELS = {
  suspension: 'Suspension Services',
  general: 'General Workshop Services',
}

const EMPTY_PROFILE = {
  height_cm: '',
  weight_kg: '',
  riding_weight_kg: '',
  riding_style: '',
  preferred_feel: '',
  preferences: '',
}

function fmtDate(value) {
  if (!value) return 'Not set'
  const raw = String(value).slice(0, 10)
  return new Date(`${raw}T12:00:00`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function money(value) {
  return `GBP ${Number(value || 0).toFixed(2)}`
}

function displayedServicePrice(service, { legacyFixed = false } = {}) {
  const price = Number(service?.base_price || 0)
  const fixedPrice = servicePricingMode(service) === 'fixed'
  if (fixedPrice && price <= 0) return ''
  if (fixedPrice && legacyFixed) return money(price)
  return service.price_label || servicePriceLabel(service)
}

function serviceModuleLabel(moduleKey) {
  return SERVICE_MODULE_LABELS[moduleKey] || 'Services'
}

function text(value, fallback = 'Not recorded') {
  return value ? String(value) : fallback
}

function isGeneralReport(report = {}) {
  return report.report_type === 'general' || report.module_key === 'general'
}

function bikeName(bike = {}) {
  return [bike.year, bike.brand, bike.model].filter(Boolean).join(' ') || 'Bike'
}

function componentLabel(component = {}) {
  return [component.year, component.brand, component.model].filter(Boolean).join(' ') || `${component.type || 'Component'}`
}

function portalBookingPath({ bikeId = '', componentId = '' } = {}) {
  const params = new URLSearchParams()
  if (bikeId) params.set('bike', bikeId)
  if (componentId) params.set('component', componentId)
  const query = params.toString()
  return query ? `/portal/book?${query}` : '/portal/book'
}

function componentTone(type = '') {
  const value = String(type).toLowerCase()
  if (value === 'fork') return 'badge-fork'
  if (value === 'shock') return 'badge-shock'
  if (value === 'dropper') return 'badge-dropper'
  return 'badge-other'
}

function verifiedLabel(value = '') {
  if (value === 'customer_submitted') return 'Customer submitted'
  if (value === 'workshop_verified') return 'Workshop verified'
  if (value === 'needs_review') return 'Needs workshop review'
  return value ? value.replace(/_/g, ' ') : 'Workshop verified'
}

function pathId(pathname, prefix) {
  if (!pathname.startsWith(prefix)) return ''
  const raw = pathname.slice(prefix.length).split('/')[0] || ''
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

function isSyntheticBikeId(id = '') {
  return String(id || '').startsWith('customer-bike:')
}

export function isValidPortalDataPayload(payload) {
  return Boolean(
    payload?.customer
    && payload?.workshop
    && Array.isArray(payload.bikes)
    && Array.isArray(payload.components)
    && Array.isArray(payload.jobs)
    && Array.isArray(payload.reports)
    && Array.isArray(payload.services)
  )
}

const SETUP_MODE_OPTIONS = [
  { value: 'improve_current', label: 'Improve current setup' },
  { value: 'baseline_check', label: 'Baseline / sense check' },
  { value: 'race_day', label: 'Race day setup' },
  { value: 'terrain_specific', label: 'Specific terrain setup' },
]

const SETUP_GOAL_OPTIONS = [
  '',
  'More support',
  'More grip',
  'More pop',
  'More comfort',
  'Calmer at speed',
  'Better climbing',
]

const TERRAIN_OPTIONS = [
  '',
  'Flow trails',
  'Natural trails',
  'Roots and rocks',
  'Steep trails',
  'Bike park',
  'Jumps',
  'Flatter local loops',
  'Wet conditions',
  'Race stage',
]

const USE_CASE_OPTIONS = [
  '',
  'Local trail riding',
  'Racing',
  'Uplift day',
  'Ride from home',
  'All-day rides',
  'Jump sessions',
]

const SYMPTOM_OPTIONS = [
  ['harsh_over_chatter', 'Harsh over chatter'],
  ['front_vague_in_corners', 'Front vague in corners'],
  ['rear_too_soft', 'Rear feels too soft'],
  ['brake_dive', 'Too much brake dive'],
  ['packs_down', 'Packs down'],
  ['kicks_on_landings', 'Kicks on landings'],
  ['no_grip', 'No grip'],
  ['feels_dead', 'Feels dead'],
  ['nervous_at_speed', 'Nervous at speed'],
  ['blows_through_travel', 'Blows through travel'],
  ['never_uses_full_travel', 'Never uses full travel'],
]

const BIKE_CATEGORY_OPTIONS = ['', 'XC', 'Trail', 'Enduro', 'DH', 'eMTB']
const WHEEL_SETUP_OPTIONS = ['', '29', 'Mullet', '27.5']
const RIDER_COMPONENT_TYPES = [
  ['fork', 'Fork'],
  ['shock', 'Shock'],
  ['dropper', 'Dropper'],
  ['brake', 'Brake'],
  ['drivetrain', 'Drivetrain'],
]
const SETUP_COMPONENT_TYPES = new Set(['fork', 'shock'])

function NavLink({ to, children }) {
  const location = useLocation()
  const active = location.pathname === to || (to !== '/portal' && location.pathname.startsWith(to))
  return <Link className={active ? 'active' : ''} to={to}>{children}</Link>
}

function PortalShell({ data, children, onSignOut }) {
  const workshop = data?.workshop || {}
  return (
    <div className="rider-portal">
      <header className="rider-header">
        <div className="logo">
          {workshop.logo_url ? <img className="app-logo-img" src={workshop.logo_url} alt={workshop.name || 'Workshop'} /> : null}
          <div className="logo-text">
            <span>{workshop.name || 'Rebound'}</span>
            <small>Rider portal</small>
          </div>
        </div>
        <nav className="rider-nav">
          <NavLink to="/portal">Dashboard</NavLink>
          <NavLink to="/portal/bikes">Bikes</NavLink>
          <NavLink to="/portal/reports">Reports</NavLink>
          <NavLink to="/portal/passport">Passport</NavLink>
          <NavLink to="/portal/profile">Profile</NavLink>
          <button className="nav-btn" onClick={onSignOut}>Sign out</button>
        </nav>
      </header>
      <main className="rider-main">{children}</main>
    </div>
  )
}

function EmptyState({ title, children }) {
  return (
    <div className="muted-empty">
      <strong className="text-white">{title}</strong>
      <div style={{ marginTop: 4 }}>{children}</div>
    </div>
  )
}

function BikeMeta({ bike }) {
  const rows = [
    ['Brand', bike.brand],
    ['Model', bike.model],
    ['Year', bike.year],
    ['Category', bike.bike_type],
    ['Size', bike.bike_size],
    ['Wheel setup', bike.wheel_setup],
    ['Travel', [bike.front_travel, bike.rear_travel].filter(Boolean).join(' / ')],
    ['Motor', bike.is_ebike ? bike.motor_system || 'E-bike' : ''],
  ]
  return (
    <div className="portal-detail-grid">
      {rows.filter(([, value]) => value).map(([label, value]) => (
        <div key={label} className="portal-detail">
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  )
}

function ComponentCard({ component, setupRecords = [], showOpenAction = true }) {
  const details = [
    ['Serial', component.serial],
    ['Damper', component.damper],
    ['Spring', component.spring_type],
    ['Travel / stroke', component.travel || component.stroke],
    ['Install length', component.install_length],
    ['Part number', component.part_number],
  ].filter(([, value]) => value)
  const setupCapable = SETUP_COMPONENT_TYPES.has(String(component.type || '').toLowerCase())
  const componentSetups = setupCapable ? setupRecords.filter(record => record.component_id === component.id) : []

  return (
    <div className="portal-component-card">
      <div className="portal-card-title-row">
        <div>
          <span className={`badge ${componentTone(component.type)}`}>{component.type || 'Component'}</span>
          <h3>{componentLabel(component)}</h3>
        </div>
        <span className="portal-pill">{verifiedLabel(component.verification_state)}</span>
      </div>
      {details.length ? (
        <div className="portal-mini-grid">
          {details.map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>
      ) : <p className="text-muted fs12">Component details will appear here once your workshop has verified them.</p>}
      {component.notes ? <p className="text-muted fs12" style={{ marginTop: 10 }}>{component.notes}</p> : null}
      {showOpenAction ? (
        <div className="action-row" style={{ marginTop: 12, justifyContent: 'space-between' }}>
          <span className="text-muted fs12">
            {setupCapable ? `${componentSetups.length} setup record${componentSetups.length === 1 ? '' : 's'}` : 'Component record'}
          </span>
          <Link className="btn btn-primary btn-sm" to={`/portal/components/${encodeURIComponent(component.id)}`}>Open Component</Link>
        </div>
      ) : null}
    </div>
  )
}

function RiderBikeForm({ token, onBikeSaved }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    brand: '',
    model: '',
    year: '',
    bike_type: '',
    bike_size: '',
    wheel_setup: '',
    front_travel: '',
    rear_travel: '',
    is_ebike: false,
    motor_system: '',
    serial: '',
    notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function update(field, value) {
    setForm(current => ({ ...current, [field]: value }))
  }

  async function save(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      const res = await fetch('/.netlify/functions/customer-portal-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'create_bike', bike: form }),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(payload.error || 'Could not add bike.')
      onBikeSaved(payload.bike)
      setForm({
        brand: '',
        model: '',
        year: '',
        bike_type: '',
        bike_size: '',
        wheel_setup: '',
        front_travel: '',
        rear_travel: '',
        is_ebike: false,
        motor_system: '',
        serial: '',
        notes: '',
      })
      setOpen(false)
    } catch (err) {
      setError(err.message || 'Could not add bike.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card">
      <div className="section-head compact">
        <div>
          <div className="card-title">Add Bike</div>
          <div className="text-muted fs12">Customer-submitted bikes are visible to your workshop for review.</div>
        </div>
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => setOpen(value => !value)}>{open ? 'Close' : '+ Add Bike'}</button>
      </div>
      {open ? (
        <form onSubmit={save}>
          {error ? <div className="alert alert-error">{error}</div> : null}
          <div className="grid-3">
            <div className="field"><label>Brand</label><input value={form.brand} onChange={e => update('brand', e.target.value)} placeholder="Norco, Santa Cruz..." /></div>
            <div className="field"><label>Model</label><input value={form.model} onChange={e => update('model', e.target.value)} placeholder="Range, Bronson..." /></div>
            <div className="field"><label>Year</label><input value={form.year} onChange={e => update('year', e.target.value)} placeholder="2026" /></div>
          </div>
          <div className="grid-3">
            <div className="field">
              <label>Category</label>
              <select value={form.bike_type} onChange={e => update('bike_type', e.target.value)}>
                {BIKE_CATEGORY_OPTIONS.map(option => <option key={option} value={option}>{option || 'Select category'}</option>)}
              </select>
            </div>
            <div className="field"><label>Size</label><input value={form.bike_size} onChange={e => update('bike_size', e.target.value)} placeholder="S3, Large..." /></div>
            <div className="field">
              <label>Wheel Setup</label>
              <select value={form.wheel_setup} onChange={e => update('wheel_setup', e.target.value)}>
                {WHEEL_SETUP_OPTIONS.map(option => <option key={option} value={option}>{option || 'Select wheels'}</option>)}
              </select>
            </div>
          </div>
          <div className="grid-3">
            <div className="field"><label>Front Travel</label><input value={form.front_travel} onChange={e => update('front_travel', e.target.value)} placeholder="170mm" /></div>
            <div className="field"><label>Rear Travel</label><input value={form.rear_travel} onChange={e => update('rear_travel', e.target.value)} placeholder="160mm" /></div>
            <label className="check-row" style={{ alignSelf: 'end' }}><input type="checkbox" checked={form.is_ebike} onChange={e => update('is_ebike', e.target.checked)} /> E-bike</label>
          </div>
          <div className="grid-2">
            <div className="field"><label>Motor System</label><input value={form.motor_system} onChange={e => update('motor_system', e.target.value)} placeholder="Bosch, Shimano..." /></div>
            <div className="field"><label>Serial</label><input value={form.serial} onChange={e => update('serial', e.target.value)} /></div>
          </div>
          <div className="field"><label>Notes</label><textarea value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Anything your workshop should know..." /></div>
          <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Add Bike'}</button>
        </form>
      ) : null}
    </section>
  )
}

function RiderComponentForm({ token, bike, onComponentSaved }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    type: 'fork',
    brand: '',
    model: '',
    year: '',
    serial: '',
    travel: '',
    install_length: '',
    part_number: '',
    notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function update(field, value) {
    setForm(current => ({ ...current, [field]: value }))
  }

  async function save(event) {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      const res = await fetch('/.netlify/functions/customer-portal-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'create_component', component: { ...form, bike_id: bike.id } }),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(payload.error || 'Could not add component.')
      onComponentSaved(payload.component)
      setForm({
        type: 'fork',
        brand: '',
        model: '',
        year: '',
        serial: '',
        travel: '',
        install_length: '',
        part_number: '',
        notes: '',
      })
      setOpen(false)
    } catch (err) {
      setError(err.message || 'Could not add component.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="card">
      <div className="section-head compact">
        <div>
          <div className="card-title">Add Component</div>
          <div className="text-muted fs12">Forks, shocks, droppers, brakes and drivetrain parts can be submitted for workshop review.</div>
        </div>
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => setOpen(value => !value)}>{open ? 'Close' : '+ Add Component'}</button>
      </div>
      {open ? (
        <form onSubmit={save}>
          {error ? <div className="alert alert-error">{error}</div> : null}
          <div className="grid-3">
            <div className="field">
              <label>Type</label>
              <select value={form.type} onChange={e => update('type', e.target.value)}>
                {RIDER_COMPONENT_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div className="field"><label>Brand</label><input value={form.brand} onChange={e => update('brand', e.target.value)} placeholder="RockShox, Fox..." /></div>
            <div className="field"><label>Model</label><input value={form.model} onChange={e => update('model', e.target.value)} placeholder="ZEB Ultimate..." /></div>
          </div>
          <div className="grid-3">
            <div className="field"><label>Year</label><input value={form.year} onChange={e => update('year', e.target.value)} placeholder="2025" /></div>
            <div className="field"><label>Serial</label><input value={form.serial} onChange={e => update('serial', e.target.value)} /></div>
            <div className="field"><label>Travel / Stroke</label><input value={form.travel} onChange={e => update('travel', e.target.value)} placeholder="170mm, 230x65..." /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Install Length / Fitment</label><input value={form.install_length} onChange={e => update('install_length', e.target.value)} /></div>
            <div className="field"><label>Part Number / Model Code</label><input value={form.part_number} onChange={e => update('part_number', e.target.value)} /></div>
          </div>
          <div className="field"><label>Notes</label><textarea value={form.notes} onChange={e => update('notes', e.target.value)} placeholder="Details copied from Trailhead, receipt, box, or bike spec..." /></div>
          <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Add Component'}</button>
        </form>
      ) : null}
    </section>
  )
}

function SetupRecordCard({ record, action }) {
  return (
    <div className="portal-component-card">
      <div className="portal-card-title-row">
        <div>
          <span className="portal-pill">{setupTypeLabel(record.setup_type)}</span>
          <h3>{record.name || setupTypeLabel(record.setup_type)}</h3>
          <p className="text-muted fs12">{record.terrain_label || 'No terrain label'}</p>
        </div>
        <div className="action-row">
          {record.is_active ? <span className="job-status-chip job-status-green">Active</span> : null}
          {record.can_edit ? <span className="job-status-chip job-status-blue">Your variant</span> : null}
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <SetupRecordSummary record={record} />
      </div>
      {record.notes ? <p className="text-muted fs12" style={{ marginTop: 10 }}>{record.notes}</p> : null}
      {action ? <div className="action-row" style={{ marginTop: 12 }}>{action}</div> : null}
    </div>
  )
}

function changeLabel(key = '') {
  return String(key || '')
    .replace(/^front_/, 'Fork ')
    .replace(/^rear_/, 'Shock ')
    .replace(/^fork_/, 'Fork ')
    .replace(/^shock_/, 'Shock ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

function SetupAdvicePanel({ data, token, bike, component, setupRecords, onRecommendationSaved }) {
  const currentSetup = setupRecords.find(record => record.setup_type === 'rider_variant' && record.is_active && record.can_edit)
    || setupRecords.find(record => record.setup_type === 'workshop_current' && record.is_active)
    || setupRecords.find(record => record.setup_type === 'workshop_current')
    || setupRecords[0]
  const [form, setForm] = useState({
    setup_mode: 'improve_current',
    setup_goal: '',
    terrain_type: '',
    use_case: '',
    symptom_tags: [],
    free_text_feedback: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)

  function setField(field, value) {
    setForm(current => ({ ...current, [field]: value }))
  }

  function toggleSymptom(value) {
    setForm(current => {
      const selected = new Set(current.symptom_tags || [])
      if (selected.has(value)) selected.delete(value)
      else selected.add(value)
      return { ...current, symptom_tags: Array.from(selected) }
    })
  }

  async function requestAdvice(event) {
    event.preventDefault()
    setLoading(true)
    setError('')
    setResult(null)
    try {
      const res = await fetch('/.netlify/functions/rider-setup-recommendation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          component_id: component.id,
          setup_snapshot_id: currentSetup?.id || '',
          ...form,
        }),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(payload.error || 'Could not generate setup advice.')
      setResult(payload)
      if (payload.recommendation && onRecommendationSaved) onRecommendationSaved(payload.recommendation)
    } catch (err) {
      setError(err.message || 'Could not generate setup advice.')
    } finally {
      setLoading(false)
    }
  }

  const profile = data.profile || {}
  const exactChanges = result?.recommendation?.exact_changes_json || {}
  const exactChangeRows = Object.entries(exactChanges).filter(([, value]) => value !== null && value !== undefined && value !== '')
  const likelyCauses = Array.isArray(result?.recommendation?.likely_causes) ? result.recommendation.likely_causes : []
  const suggestedActions = Array.isArray(result?.recommendation?.suggested_actions) ? result.recommendation.suggested_actions : []

  return (
    <section className="card">
      <div className="section-head compact">
        <div>
          <div className="card-title">Improve Setup</div>
          <div className="text-muted fs12">Uses your profile, bike, component and current verified setup. Add only the thing you want to improve.</div>
        </div>
        <span className="job-status-chip job-status-blue">Setup AI</span>
      </div>

      <div className="portal-context-grid">
        <div><span>Rider profile</span><strong>{profile.riding_weight_kg ? `${profile.riding_weight_kg} kg riding weight` : 'No riding weight yet'}</strong></div>
        <div><span>Bike</span><strong>{[bike.bike_type, bike.wheel_setup, bike.bike_size].filter(Boolean).join(' / ') || bikeName(bike)}</strong></div>
        <div><span>Current setup</span><strong>{currentSetup ? currentSetup.name || setupTypeLabel(currentSetup.setup_type) : 'No setup record yet'}</strong></div>
      </div>

      <form onSubmit={requestAdvice} style={{ marginTop: 14 }}>
        {error ? <div className="alert alert-error">{error}</div> : null}
        <div className="grid-2">
          <div className="field">
            <label>Request type</label>
            <select value={form.setup_mode} onChange={event => setField('setup_mode', event.target.value)}>
              {SETUP_MODE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Goal</label>
            <select value={form.setup_goal} onChange={event => setField('setup_goal', event.target.value)}>
              {SETUP_GOAL_OPTIONS.map(option => <option key={option || 'blank'} value={option}>{option || 'Choose a goal...'}</option>)}
            </select>
          </div>
        </div>
        <div className="grid-2">
          <div className="field">
            <label>Terrain / trail type</label>
            <select value={form.terrain_type} onChange={event => setField('terrain_type', event.target.value)}>
              {TERRAIN_OPTIONS.map(option => <option key={option || 'blank'} value={option}>{option || 'Choose terrain...'}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Use case</label>
            <select value={form.use_case} onChange={event => setField('use_case', event.target.value)}>
              {USE_CASE_OPTIONS.map(option => <option key={option || 'blank'} value={option}>{option || 'Choose use case...'}</option>)}
            </select>
          </div>
        </div>
        <div className="field">
          <label>Symptoms</label>
          <div className="portal-symptom-grid">
            {SYMPTOM_OPTIONS.map(([value, label]) => (
              <label key={value} className="check-item">
                <input type="checkbox" checked={(form.symptom_tags || []).includes(value)} onChange={() => toggleSymptom(value)} />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="field">
          <label>Feedback / what do you want to improve?</label>
          <textarea
            value={form.free_text_feedback}
            onChange={event => setField('free_text_feedback', event.target.value)}
            placeholder="e.g. Feels loose in corners, harsh on repeated roots, or I want a calmer race day setup..."
          />
        </div>
        <button className="btn btn-primary" type="submit" disabled={loading}>
          {loading ? 'Checking setup...' : 'Get Setup Advice'}
        </button>
      </form>

      {result?.recommendation ? (
        <div className="portal-recommendation">
          <div className="portal-card-title-row">
            <div>
              <span className="portal-pill">{result.recommendation.confidence_level} confidence</span>
              <h3>{result.recommendation.summary || 'Setup advice'}</h3>
            </div>
            {result.context?.ai_available ? <span className="job-status-chip job-status-green">AI assisted</span> : <span className="job-status-chip job-status-orange">Review needed</span>}
          </div>
          {suggestedActions.length ? (
            <div className="recommendation-block">
              <div className="card-title">Try this first</div>
              <ul>{suggestedActions.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
            </div>
          ) : null}
          {exactChangeRows.length ? (
            <div className="portal-mini-grid" style={{ marginTop: 12 }}>
              {exactChangeRows.map(([key, value]) => (
                <div key={key}>
                  <span>{changeLabel(key)}</span>
                  <strong>{String(value)}</strong>
                </div>
              ))}
            </div>
          ) : null}
          <div className="grid-2" style={{ marginTop: 12 }}>
            <div className="recommendation-block">
              <div className="card-title">Why</div>
              <p>{result.recommendation.rationale || 'Rebound used your stored rider, bike and setup data to produce this advice.'}</p>
            </div>
            <div className="recommendation-block">
              <div className="card-title">Test plan</div>
              <p>{result.recommendation.test_instruction || 'Try one change at a time on a familiar trail section.'}</p>
            </div>
          </div>
          {likelyCauses.length ? (
            <div className="recommendation-block" style={{ marginTop: 12 }}>
              <div className="card-title">Likely causes</div>
              <ul>{likelyCauses.map((item, index) => <li key={index}>{item.cause || item.reason || String(item)}</li>)}</ul>
            </div>
          ) : null}
          {result.recommendation.tradeoff ? <p className="text-muted fs12" style={{ marginTop: 12 }}>Tradeoff: {result.recommendation.tradeoff}</p> : null}
          {result.recommendation.requires_workshop_review ? <p className="text-muted fs12" style={{ marginTop: 8 }}>Workshop review recommended before treating this as a verified setup.</p> : null}
        </div>
      ) : null}
    </section>
  )
}

function RecommendationCard({ recommendation }) {
  const exactChanges = recommendation?.exact_changes_json || {}
  const exactChangeRows = Object.entries(exactChanges).filter(([, value]) => value !== null && value !== undefined && value !== '')
  const suggestedActions = Array.isArray(recommendation?.suggested_actions) ? recommendation.suggested_actions : []
  const likelyCauses = Array.isArray(recommendation?.likely_causes) ? recommendation.likely_causes : []

  return (
    <div className="portal-component-card">
      <div className="portal-card-title-row">
        <div>
          <span className="portal-pill">{recommendation.confidence_level || 'low'} confidence</span>
          <h3>{recommendation.summary || 'Setup recommendation'}</h3>
          <p className="text-muted fs12">{recommendation.created_at ? fmtDate(recommendation.created_at) : 'Recommendation history'}</p>
        </div>
        <div className="action-row">
          {recommendation.ai_available ? <span className="job-status-chip job-status-green">AI assisted</span> : <span className="job-status-chip job-status-orange">Review needed</span>}
          {recommendation.recommendation_status ? <span className="job-status-chip">{recommendation.recommendation_status.replace(/_/g, ' ')}</span> : null}
        </div>
      </div>
      {suggestedActions.length ? (
        <div className="recommendation-block" style={{ marginTop: 12 }}>
          <div className="card-title">Try this first</div>
          <ul>{suggestedActions.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>
        </div>
      ) : null}
      {exactChangeRows.length ? (
        <div className="portal-mini-grid" style={{ marginTop: 12 }}>
          {exactChangeRows.map(([key, value]) => (
            <div key={key}>
              <span>{changeLabel(key)}</span>
              <strong>{String(value)}</strong>
            </div>
          ))}
        </div>
      ) : null}
      {recommendation.rationale ? <p className="text-muted fs12" style={{ marginTop: 12 }}>{recommendation.rationale}</p> : null}
      {likelyCauses.length ? (
        <div className="text-muted fs12" style={{ marginTop: 8 }}>
          Likely causes: {likelyCauses.map(item => item.cause || item.reason || String(item)).join(', ')}
        </div>
      ) : null}
      {recommendation.requires_workshop_review ? <p className="text-muted fs12" style={{ marginTop: 8 }}>Workshop review recommended before treating this as a verified setup.</p> : null}
    </div>
  )
}

function RecommendationHistory({ recommendations = [] }) {
  return (
    <section className="card">
      <div className="section-head compact">
        <div>
          <div className="card-title">Recommendation History</div>
          <div className="text-muted fs12">Setup advice saved against this exact component.</div>
        </div>
        <span className="portal-pill">{recommendations.length} saved</span>
      </div>
      {recommendations.length ? (
        <div className="portal-component-grid">
          {recommendations.map(recommendation => <RecommendationCard key={recommendation.id} recommendation={recommendation} />)}
        </div>
      ) : (
        <EmptyState title="No recommendations yet">Ask for setup advice and it will be saved here against this component.</EmptyState>
      )}
    </section>
  )
}

function ReportRow({ report }) {
  const generalReport = isGeneralReport(report)
  return (
    <Link className="portal-report-row" to={`/portal/reports/${encodeURIComponent(report.id)}`}>
      <div>
        <strong>{fmtDate(report.date)}</strong>
        <span>{report.service_summary}</span>
        {generalReport ? (
          <span>{report.bike_name || 'General Workshop service'}</span>
        ) : report.component_name || report.bike_name ? (
          <span>{[report.bike_name, report.component_name].filter(Boolean).join(' - ')}</span>
        ) : null}
      </div>
      <div>
        <strong>{generalReport ? 'General report' : money(report.total_charged)}</strong>
        <span>{report.delivery_status === 'sent' ? 'Sent report' : 'Saved report'}</span>
      </div>
    </Link>
  )
}

function Dashboard({ data, bikeComponents, latestReport }) {
  const bikeCount = data.bikes.length
  const reportCount = data.reports.length
  const due = data.next_due
  const bookingLink = portalBookingPath()

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Hi {data.customer.first_name || data.customer.name}</div>
          <div className="text-muted fs12 mt4">Your bikes, suspension history and service reminders with {data.workshop.name}.</div>
        </div>
        <div className="page-actions">
          <Link className="btn btn-primary" to={bookingLink}>Book Service</Link>
          <Link className="btn btn-ghost" to="/portal/profile">Profile / Preferences</Link>
        </div>
      </div>

      <div className="portal-summary-grid">
        <div className="card compact-card">
          <div className="card-title">Bikes</div>
          <div className="portal-stat">{bikeCount}</div>
          <div className="text-muted fs12">linked to your profile</div>
        </div>
        <div className="card compact-card">
          <div className="card-title">Service Reports</div>
          <div className="portal-stat">{reportCount}</div>
          <div className="text-muted fs12">customer-visible records</div>
        </div>
        <div className="card compact-card">
          <div className="card-title">Next Service Due</div>
          <div className="portal-due">{due ? fmtDate(due.date) : 'Not set'}</div>
          <div className="text-muted fs12">{due?.label || 'No reminder has been set yet'}</div>
        </div>
      </div>

      <section className="card">
        <div className="section-head compact">
          <div>
            <div className="card-title">My Bikes</div>
            <div className="text-muted fs12">Workshop verified bike and suspension records.</div>
          </div>
          <Link className="btn btn-ghost btn-sm" to="/portal/bikes">View Bikes</Link>
        </div>
        {!data.bikes.length ? (
          <EmptyState title="No bikes yet">Your workshop has not linked a bike to your rider profile yet.</EmptyState>
        ) : (
          <div className="portal-bike-grid">
            {data.bikes.slice(0, 3).map(bike => (
              <Link key={bike.id} className="portal-bike-card" to={`/portal/bikes/${encodeURIComponent(bike.id)}`}>
                <div>
                  <span className="portal-pill">{bike.is_ebike ? 'E-bike' : bike.bike_type || 'Bike'}</span>
                  <h3>{bikeName(bike)}</h3>
                  <p>{(bikeComponents[bike.id] || []).length} component{(bikeComponents[bike.id] || []).length === 1 ? '' : 's'}</p>
                </div>
                <span>View Bike</span>
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="grid-2">
        <div className="card">
          <div className="section-head compact">
            <div>
              <div className="card-title">Latest Service Report</div>
              <div className="text-muted fs12">Your most recent customer report.</div>
            </div>
            <Link className="btn btn-ghost btn-sm" to="/portal/reports">Reports</Link>
          </div>
          {latestReport ? <ReportRow report={latestReport} /> : (
            <EmptyState title="No reports yet">Customer-visible service reports will appear here when your workshop creates them.</EmptyState>
          )}
        </div>
        <div className="card">
          <div className="card-title">Quick Actions</div>
          <div className="portal-action-list">
            <Link to="/portal/bikes">View Bike</Link>
            <Link to="/portal/reports">View Service Reports</Link>
            <Link to="/portal/passport">Service Passport</Link>
            <Link to={bookingLink}>Book Service</Link>
            <Link to="/portal/profile">Profile / Preferences</Link>
          </div>
        </div>
      </section>
    </>
  )
}

function BikesList({ data, token, bikeComponents, onBikeSaved }) {
  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">My Bikes</div>
          <div className="text-muted fs12 mt4">Bikes and components linked by your workshop or submitted for review.</div>
        </div>
        <Link className="btn btn-primary" to={portalBookingPath()}>Book Service</Link>
      </div>

      <RiderBikeForm token={token} onBikeSaved={onBikeSaved} />

      {!data.bikes.length ? (
        <div className="card"><EmptyState title="No bikes linked">Add a bike here, or ask your workshop to link one from a service booking.</EmptyState></div>
      ) : (
        <div className="portal-bike-grid">
          {data.bikes.map(bike => (
            <Link key={bike.id} className="portal-bike-card" to={`/portal/bikes/${encodeURIComponent(bike.id)}`}>
              <div>
                <span className="portal-pill">{verifiedLabel(bike.verification_state)}</span>
                <h3>{bikeName(bike)}</h3>
                <p>{[bike.bike_type, bike.is_ebike ? 'E-bike' : '', bike.motor_system].filter(Boolean).join(' - ') || 'Bike details'}</p>
              </div>
              <span>{(bikeComponents[bike.id] || []).length} component{(bikeComponents[bike.id] || []).length === 1 ? '' : 's'}</span>
            </Link>
          ))}
        </div>
      )}
    </>
  )
}

function BikeDetail({ data, token, bikeComponents, bikeId, onComponentSaved }) {
  const bike = data.bikes.find(item => item.id === bikeId)
  const components = bike ? bikeComponents[bike.id] || [] : []
  const reports = bike ? data.reports.filter(report => {
    const componentIds = new Set(components.map(component => component.id))
    return data.jobs.some(job => job.bike_id === bike.id && job.id === report.job_id)
      || componentIds.has(report.component_id)
  }) : []

  if (!bike) return <div className="card"><EmptyState title="Bike not found">This bike is not linked to your rider profile.</EmptyState></div>

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">{bikeName(bike)}</div>
          <div className="text-muted fs12 mt4">Bike detail and linked components.</div>
        </div>
        <div className="page-actions">
          <Link className="btn btn-primary" to={portalBookingPath({ bikeId: bike.id })}>Book Service</Link>
          <Link className="btn btn-ghost" to="/portal/bikes">Back to bikes</Link>
        </div>
      </div>

      <section className="card">
        <div className="section-head compact">
          <div>
            <div className="card-title">Bike Details</div>
            <div className="text-muted fs12">Workshop verified unless marked otherwise.</div>
          </div>
          <span className="portal-pill">{verifiedLabel(bike.verification_state)}</span>
        </div>
        <BikeMeta bike={bike} />
        {bike.notes ? <p className="text-muted fs12" style={{ marginTop: 14 }}>{bike.notes}</p> : null}
      </section>

      <section className="card">
        <div className="card-title">Components</div>
        {!components.length ? (
          <EmptyState title="No components linked">Add a component here, or your workshop can link one from a service booking.</EmptyState>
        ) : (
          <div className="portal-component-grid">
            {components.map(component => <ComponentCard key={component.id} component={component} setupRecords={data.setup_records || []} />)}
          </div>
        )}
      </section>

      <RiderComponentForm token={token} bike={bike} onComponentSaved={onComponentSaved} />

      <section className="card">
        <div className="section-head compact">
          <div>
            <div className="card-title">Reports For This Bike</div>
            <div className="text-muted fs12">Customer-visible service history.</div>
          </div>
          <Link className="btn btn-ghost btn-sm" to="/portal/reports">All Reports</Link>
        </div>
        {reports.length ? reports.map(report => <ReportRow key={report.id} report={report} />) : (
          <EmptyState title="No reports yet">No customer-visible reports have been saved against this bike yet.</EmptyState>
        )}
      </section>
    </>
  )
}

function ComponentDetailPage({ data, token, componentId, onSetupSaved, onRecommendationSaved }) {
  const component = data.components.find(item => item.id === componentId)
  const bike = component ? data.bikes.find(item => item.id === component.bike_id) : null
  const setupRecords = component ? (data.setup_records || []).filter(record => record.component_id === component.id) : []
  const editableRecords = setupRecords.filter(record => record.can_edit)
  const recommendations = component ? (data.recommendations || []).filter(record => record.component_id === component.id) : []
  const setupCapable = component ? SETUP_COMPONENT_TYPES.has(String(component.type || '').toLowerCase()) : false
  const [editingId, setEditingId] = useState('')
  const [form, setForm] = useState(() => ({ ...SETUP_EMPTY, name: defaultSetupName('rider_variant'), setup_type: 'rider_variant' }))
  const [saving, setSaving] = useState(false)
  const [activatingId, setActivatingId] = useState('')
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const variantFormRef = useRef(null)

  useEffect(() => {
    const row = setupRecords.find(record => record.id === editingId)
    if (row) setForm(toSetupForm(row, { setup_type: 'rider_variant' }))
  }, [editingId, setupRecords])

  if (!component) return <div className="card"><EmptyState title="Component not found">This component is not linked to your rider profile.</EmptyState></div>
  if (!bike) return <div className="card"><EmptyState title="Bike link missing">Ask your workshop to link this component to a real bike before setup variants can be saved.</EmptyState></div>
  const setupBlocked = isSyntheticBikeId(bike.id)

  function focusVariantForm() {
    window.setTimeout(() => {
      variantFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      variantFormRef.current?.focus?.({ preventScroll: true })
    }, 0)
  }

  function startBlank() {
    setEditingId('')
    setForm({ ...SETUP_EMPTY, name: defaultSetupName('rider_variant'), setup_type: 'rider_variant', is_active: false })
    setNotice('New rider variant ready. Fill in the setup below and save it when done.')
    setError('')
    focusVariantForm()
  }

  function duplicateRecord(record) {
    setEditingId('')
    setForm({
      ...toSetupForm(record),
      name: `Copy of ${record.name || setupTypeLabel(record.setup_type)}`,
      setup_type: 'rider_variant',
      is_active: false,
    })
    setNotice('')
    setError('')
    focusVariantForm()
  }

  function editOwnRecord(record) {
    setEditingId(record.id)
    setForm(toSetupForm(record))
    setNotice('')
    setError('')
    focusVariantForm()
  }

  async function saveSetup(event) {
    event.preventDefault()
    setSaving(true)
    setNotice('')
    setError('')
    try {
      const payload = {
        ...setupPayloadFromForm({ ...form, setup_type: 'rider_variant' }),
        id: editingId || undefined,
        bike_id: bike.id,
        component_id: component.id,
      }
      const res = await fetch('/.netlify/functions/customer-portal-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: editingId ? 'update_setup_variant' : 'create_setup_variant',
          setup: payload,
        }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || 'Could not save setup record.')
      onSetupSaved(body.setup)
      setEditingId(body.setup.id)
      setForm(toSetupForm(body.setup))
      setNotice('Setup variant saved.')
    } catch (err) {
      setError(err.message || 'Could not save setup record.')
    } finally {
      setSaving(false)
    }
  }

  async function setActiveVariant(record) {
    setActivatingId(record.id)
    setNotice('')
    setError('')
    try {
      const res = await fetch('/.netlify/functions/customer-portal-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'set_active_setup_variant',
          setup_id: record.id,
        }),
      })
      const body = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(body.error || 'Could not mark setup as in use.')
      onSetupSaved(body.setup)
      setNotice('Current rider setup updated.')
    } catch (err) {
      setError(err.message || 'Could not mark setup as in use.')
    } finally {
      setActivatingId('')
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">{componentLabel(component)}</div>
          <div className="text-muted fs12 mt4">
            {setupCapable ? `${bikeName(bike)} - workshop verified component setup and rider variants.` : `${bikeName(bike)} - component record.`}
          </div>
        </div>
          <div className="page-actions">
          <Link className="btn btn-ghost" to={`/portal/bikes/${encodeURIComponent(bike.id)}`}>Back to bike</Link>
          <Link className="btn btn-ghost" to={portalBookingPath({ bikeId: bike.id, componentId: component.id })}>Book Service</Link>
          {setupCapable ? <button className="btn btn-primary" type="button" onClick={startBlank}>New Rider Variant</button> : null}
        </div>
      </div>

      {notice ? <div className="alert alert-success">{notice}</div> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}

      <section className="card">
        <div className="section-head compact">
          <div>
            <div className="card-title">Component Details</div>
            <div className="text-muted fs12">Verified by your workshop unless marked otherwise.</div>
          </div>
          <span className="portal-pill">{verifiedLabel(component.verification_state)}</span>
        </div>
        <ComponentCard component={component} setupRecords={setupRecords} showOpenAction={false} />
      </section>

      {!setupCapable ? (
        <section className="card">
          <EmptyState title="Setup tools are for fork and shock records">This component is saved on your rider profile, but suspension setup records and AI advice are only shown for fork and shock components.</EmptyState>
        </section>
      ) : setupBlocked ? (
        <section className="card">
          <EmptyState title="Setup records need a verified bike">Ask your workshop to link this component to a real bike before setup variants can be saved.</EmptyState>
        </section>
      ) : (
        <>
          <section className="card">
            <div className="section-head compact">
              <div>
                <div className="card-title">Setup Records</div>
                <div className="text-muted fs12">Workshop records are read-only. Rider variants are yours to edit.</div>
              </div>
            </div>
            {setupRecords.length ? (
              <div className="portal-component-grid">
                {setupRecords.map(record => (
                  <SetupRecordCard
                    key={record.id}
                    record={record}
                    action={record.can_edit ? (
                      <>
                        <button className="btn btn-ghost btn-sm" type="button" onClick={() => editOwnRecord(record)}>Edit</button>
                        <button className="btn btn-primary btn-sm" type="button" onClick={() => duplicateRecord(record)}>Duplicate</button>
                        {!record.is_active ? (
                          <button className="btn btn-ghost btn-sm" type="button" onClick={() => setActiveVariant(record)} disabled={activatingId === record.id}>
                            {activatingId === record.id ? 'Updating...' : 'Set In Use'}
                          </button>
                        ) : null}
                      </>
                    ) : (
                      <button className="btn btn-primary btn-sm" type="button" onClick={() => duplicateRecord(record)}>Use As Starting Point</button>
                    )}
                  />
                ))}
              </div>
            ) : (
              <EmptyState title="No setup records yet">Create your first rider setup variant, or ask your workshop to save the verified setup after a service.</EmptyState>
            )}
          </section>

          <SetupAdvicePanel
            data={data}
            token={token}
            bike={bike}
            component={component}
            setupRecords={setupRecords}
            onRecommendationSaved={onRecommendationSaved}
          />

          <RecommendationHistory recommendations={recommendations} />

          <section className="card" ref={variantFormRef} tabIndex="-1">
            <div className="section-head compact">
              <div>
                <div className="card-title">{editingId ? 'Edit Rider Variant' : 'New Rider Variant'}</div>
                <div className="text-muted fs12">
                  {editableRecords.length ? `${editableRecords.length} rider variant${editableRecords.length === 1 ? '' : 's'} saved.` : 'This will create a rider-owned setup record.'}
                </div>
              </div>
              <span className="job-status-chip job-status-blue">Rider Variant</span>
            </div>
            <SetupRecordForm
              form={{ ...form, setup_type: 'rider_variant' }}
              setForm={setForm}
              canEdit
              canChooseType={false}
              allowedTypes={['rider_variant']}
              componentType={component.type}
              onSave={saveSetup}
              onCancel={startBlank}
              saving={saving}
              submitLabel={editingId ? 'Update My Setup' : 'Save My Setup'}
            />
          </section>
        </>
      )}
    </>
  )
}

function ReportsList({ data, token }) {
  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Service Reports</div>
          <div className="text-muted fs12 mt4">Customer-visible service records from your workshop.</div>
        </div>
      </div>
      <section className="card">
        {data.reports.length ? data.reports.map(report => <ReportRow key={report.id} report={report} />) : (
          <EmptyState title="No reports yet">Reports will appear here after your workshop creates a customer report.</EmptyState>
        )}
      </section>
      <RestoredCustomerEvidence token={token} />
    </>
  )
}

function passportComponentTitle(entry = {}) {
  const identity = [entry.component_brand, entry.component_model].filter(Boolean).join(' ')
  return identity || entry.component_type || 'Suspension component'
}

function PassportEntry({ entry }) {
  return (
    <div className="service-passport-entry">
      <div>
        <span className="portal-pill">{entry.component_type}</span>
        <h3>{passportComponentTitle(entry)}</h3>
        {entry.component_serial ? <p>Serial: {entry.component_serial}</p> : null}
      </div>
      <div className="service-passport-service">
        <strong>{entry.service_name}</strong>
        <span>{fmtDate(entry.service_date)} - {entry.service_type || entry.component_type}</span>
        {entry.upgrades_installed?.length ? (
          <small>Upgrades: {entry.upgrades_installed.join(', ')}</small>
        ) : <small>No add-on upgrades recorded</small>}
      </div>
    </div>
  )
}

function PassportPage({ token }) {
  const [loading, setLoading] = useState(true)
  const [enabled, setEnabled] = useState(false)
  const [entries, setEntries] = useState([])
  const [generatedAt, setGeneratedAt] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    async function loadPassport() {
      setLoading(true)
      setError('')
      try {
        const res = await fetch('/.netlify/functions/service-passport', {
          headers: { Authorization: `Bearer ${token}` },
        })
        const payload = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(payload.error || 'Could not load Service Passport.')
        if (alive) {
          setEnabled(payload.enabled === true)
          setEntries(Array.isArray(payload.entries) ? payload.entries : [])
          setGeneratedAt(payload.generated_at || '')
        }
      } catch (err) {
        if (alive) setError(err.message || 'Could not load Service Passport.')
      } finally {
        if (alive) setLoading(false)
      }
    }
    loadPassport()
    return () => { alive = false }
  }, [token])

  function printPassport() {
    if (typeof window !== 'undefined') window.print()
  }

  return (
    <>
      <div className="page-head service-passport-no-print">
        <div>
          <div className="page-title">Suspension Service Passport</div>
          <div className="text-muted fs12 mt4">Minimal completed and paid suspension service history for your fork, shock and dropper components.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" type="button" onClick={printPassport} disabled={!enabled || !entries.length}>Print / Save PDF</button>
          <Link className="btn btn-ghost" to="/portal">Back</Link>
        </div>
      </div>

      {error ? <div className="alert alert-error service-passport-no-print">{error}</div> : null}
      {loading ? <div className="empty service-passport-no-print"><span className="spinner" style={{ width: 20, height: 20 }} /></div> : null}

      {!loading && !enabled ? (
        <div className="card service-passport-no-print">
          <EmptyState title="Service Passport not enabled">Your workshop has not enabled customer Service Passport downloads yet.</EmptyState>
        </div>
      ) : null}

      {!loading && enabled ? (
        <section className="card service-passport-sheet">
          <div className="service-passport-brand">
            <div>
              <span>REBOUND</span>
              <h1>Suspension Service Passport</h1>
            </div>
            <small>{generatedAt ? `Generated ${fmtDate(generatedAt.slice(0, 10))}` : ''}</small>
          </div>
          <div className="service-passport-note">
            Completed and paid suspension service history only. This passport excludes workshop identity, customer contact details, prices, payment information, reports, photos, setup data and internal notes.
          </div>
          {entries.length ? (
            <div className="service-passport-entry-list">
              {entries.map((entry, index) => <PassportEntry key={`${entry.service_date}-${index}`} entry={entry} />)}
            </div>
          ) : (
            <EmptyState title="No passport entries yet">Completed and paid fork, shock or dropper services will appear here when your workshop records them.</EmptyState>
          )}
        </section>
      ) : null}
    </>
  )
}

function PortalBookingPage({ data, token }) {
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)
  const initialBikeId = params.get('bike') || ''
  const initialComponentId = params.get('component') || ''
  const [form, setForm] = useState({
    bike_id: initialBikeId,
    component_id: initialComponentId,
    service_ids: [],
    preferred_date: '',
    notes: '',
  })
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')
  const [serviceModuleFilter, setServiceModuleFilter] = useState('suspension')
  const [serviceCategoryFilter, setServiceCategoryFilter] = useState('all')
  const [serviceSearch, setServiceSearch] = useState('')

  const selectedBike = data.bikes.find(bike => bike.id === form.bike_id) || null
  const componentOptions = data.components.filter(component => !form.bike_id || component.bike_id === form.bike_id)
  const selectedComponent = data.components.find(component => component.id === form.component_id) || null
  const selectedServices = (data.services || [])
    .filter(service => form.service_ids.includes(service.id))
  const servicePricing = bookingPricingSummary(selectedServices)
  const selectedHasGeneralService = selectedServices.some(service => serviceModuleKey(service) === 'general')
  const selectedHasSuspensionService = selectedServices.some(service => serviceModuleKey(service) === 'suspension')
  const selectedGeneralOnly = selectedHasGeneralService && !selectedHasSuspensionService
  const selectedMixedModules = selectedHasGeneralService && selectedHasSuspensionService
  const generalContextProvided = Boolean(form.bike_id || form.component_id || String(form.notes || '').trim())
  const generalWorkshopEnabled = data.workshop?.general_workshop_enabled === true
  const selectedFixedTotal = selectedServices.reduce((sum, service) => sum + Number(service.base_price || 0), 0)
  const selectedTotalLabel = generalWorkshopEnabled
    ? servicePricing.total_label
    : selectedFixedTotal > 0 ? money(selectedFixedTotal) : ''
  const showServiceNavigation = generalWorkshopEnabled && (data.services || []).length > 0
  const availableServiceModules = useMemo(() => {
    if (!generalWorkshopEnabled) return ['suspension']
    return SERVICE_MODULE_ORDER.filter(moduleKey =>
      (data.services || []).some(service => serviceModuleKey(service) === moduleKey)
    )
  }, [data.services, generalWorkshopEnabled])
  const serviceSections = useMemo(() => {
    const publishedServices = data.services || []
    if (!generalWorkshopEnabled) return [{ key: 'published', title: '', groups: [['', publishedServices]] }]

    return SERVICE_MODULE_ORDER
      .map(moduleKey => {
        const moduleServices = publishedServices.filter(service => serviceModuleKey(service) === moduleKey)
        const groups = moduleServices.reduce((acc, service) => {
          const label = serviceCategoryLabel(service)
          if (!acc[label]) acc[label] = []
          acc[label].push(service)
          return acc
        }, {})
        return {
          key: moduleKey,
          title: moduleKey === 'general' ? 'General Workshop Services' : 'Suspension Services',
          groups: Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)),
        }
      })
      .filter(section => section.groups.length > 0)
  }, [data.services, generalWorkshopEnabled])
  const activeServiceSection = serviceSections.find(section => section.key === serviceModuleFilter) || serviceSections[0] || null
  const effectiveCategoryFilter = showServiceNavigation ? serviceCategoryFilter : 'all'
  const serviceCategoryOptions = activeServiceSection
    ? activeServiceSection.groups.map(([group, list]) => ({
      value: group,
      label: group || 'Services',
      count: list.length,
    }))
    : []
  const normalisedServiceSearch = showServiceNavigation ? serviceSearch.trim().toLowerCase() : ''
  const visibleServiceGroups = activeServiceSection
    ? activeServiceSection.groups
      .map(([group, list]) => {
        const filtered = list.filter(service => {
          if (effectiveCategoryFilter !== 'all' && group !== effectiveCategoryFilter) return false
          if (!normalisedServiceSearch) return true
          return [
            service.name,
            service.description,
            service.brand,
            service.category,
            serviceCategoryLabel(service),
            service.booking_label || serviceBookingLabel(service),
          ].filter(Boolean).join(' ').toLowerCase().includes(normalisedServiceSearch)
        })
        return [group, filtered]
      })
      .filter(([, list]) => list.length > 0)
    : []
  const visibleServiceCount = visibleServiceGroups.reduce((sum, [, list]) => sum + list.length, 0)

  useEffect(() => {
    const component = data.components.find(item => item.id === form.component_id)
    if (component?.bike_id && component.bike_id !== form.bike_id) {
      setForm(current => ({ ...current, bike_id: component.bike_id }))
    }
  }, [data.components, form.component_id, form.bike_id])

  useEffect(() => {
    if (!generalWorkshopEnabled) {
      if (serviceModuleFilter !== 'suspension') setServiceModuleFilter('suspension')
      return
    }
    const fallback = availableServiceModules[0] || 'suspension'
    if (availableServiceModules.length && !availableServiceModules.includes(serviceModuleFilter)) {
      setServiceModuleFilter(fallback)
    }
  }, [availableServiceModules, generalWorkshopEnabled, serviceModuleFilter])

  useEffect(() => {
    setServiceCategoryFilter('all')
  }, [serviceModuleFilter])

  function update(field, value) {
    setForm(current => ({ ...current, [field]: value }))
  }

  function toggleService(serviceId) {
    setForm(current => {
      const exists = current.service_ids.includes(serviceId)
      return {
        ...current,
        service_ids: exists
          ? current.service_ids.filter(id => id !== serviceId)
          : [...current.service_ids, serviceId],
      }
    })
  }

  function updateBike(value) {
    setForm(current => ({
      ...current,
      bike_id: value,
      component_id: current.component_id && data.components.some(component => component.id === current.component_id && component.bike_id === value)
        ? current.component_id
        : '',
    }))
  }

  async function submitBooking(event) {
    event.preventDefault()
    if (selectedHasGeneralService && !generalContextProvided) {
      setNotice('')
      setError('Choose a bike or add notes so the workshop knows what the General service relates to.')
      return
    }
    setSaving(true)
    setNotice('')
    setError('')
    try {
      const res = await fetch('/.netlify/functions/customer-portal-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: 'create_booking_request',
          booking: form,
        }),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(payload.error || 'Could not send booking request.')
      setNotice('Booking request sent to your workshop.')
      setForm(current => ({ ...current, service_ids: [], preferred_date: '', notes: '' }))
    } catch (err) {
      setError(err.message || 'Could not send booking request.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Book Service</div>
          <div className="text-muted fs12 mt4">Send a request to {data.workshop.name} using your linked rider profile.</div>
        </div>
        <button className="btn btn-ghost" type="button" onClick={() => navigate(-1)}>Back</button>
      </div>

      {notice ? <div className="alert alert-success">{notice}</div> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}

      <form className="card" onSubmit={submitBooking}>
        <div className="section-head compact">
          <div>
            <div className="card-title">Service Request</div>
            <div className="text-muted fs12">Your name, email and phone are taken from your workshop customer record.</div>
          </div>
          {selectedTotalLabel ? <span className="portal-pill">{selectedTotalLabel}</span> : null}
        </div>

        <div className="grid-2">
          <div className="field">
            <label>{selectedHasGeneralService ? 'Bike / Service Context' : 'Bike'}</label>
            <select value={form.bike_id} onChange={e => updateBike(e.target.value)}>
              <option value="">{selectedHasGeneralService ? 'Choose a bike or describe in notes' : 'General request / choose later'}</option>
              {data.bikes.map(bike => <option key={bike.id} value={bike.id}>{bikeName(bike)}</option>)}
            </select>
          </div>
          <div className="field">
            <label>{selectedGeneralOnly ? 'Component / Item (optional)' : selectedMixedModules ? 'Suspension Component (if known)' : 'Component'}</label>
            <select value={form.component_id} onChange={e => update('component_id', e.target.value)}>
              <option value="">Choose later</option>
              {componentOptions.map(component => <option key={component.id} value={component.id}>{componentLabel(component)}</option>)}
            </select>
          </div>
        </div>

        {(selectedBike || selectedComponent) ? (
          <div className="portal-context-grid" style={{ marginBottom: 14 }}>
            {selectedBike ? <div><span>Bike</span><strong>{bikeName(selectedBike)}</strong></div> : null}
            {selectedComponent ? <div><span>Component</span><strong>{componentLabel(selectedComponent)}</strong></div> : null}
            {selectedComponent?.serial ? <div><span>Serial</span><strong>{selectedComponent.serial}</strong></div> : null}
          </div>
        ) : null}

        {selectedHasGeneralService && !selectedBike && !selectedComponent ? (
          <div className="alert alert-info" style={{ marginBottom: 14 }}>
            {selectedMixedModules
              ? 'Choose a bike or add notes for the General service. Component details are useful for the Suspension service if you know them.'
              : 'Choose a bike or add notes so the workshop knows what bike, item, area, or problem this General service relates to.'}
          </div>
        ) : null}

        <div className="field">
          <label>Preferred Date</label>
          <input type="date" value={form.preferred_date} onChange={e => update('preferred_date', e.target.value)} required />
        </div>

        <div className="portal-service-picker">
          <div className="card-title">Service Menu</div>
          {(data.services || []).length ? (
            <>
              {showServiceNavigation && availableServiceModules.length > 1 ? (
                <div className="booking-service-tabs portal-service-tabs">
                  {availableServiceModules.map(moduleKey => (
                    <button
                      type="button"
                      key={moduleKey}
                      className={serviceModuleFilter === moduleKey ? 'active' : ''}
                      onClick={() => setServiceModuleFilter(moduleKey)}
                    >
                      {serviceModuleLabel(moduleKey)}
                    </button>
                  ))}
                </div>
              ) : null}
              {showServiceNavigation ? (
                <div className="booking-service-tools portal-service-tools">
                  <input
                    type="search"
                    value={serviceSearch}
                    onChange={event => setServiceSearch(event.target.value)}
                    placeholder="Search services..."
                    aria-label="Search services"
                  />
                  {serviceCategoryOptions.length > 1 ? (
                    <div className="booking-service-filters portal-service-filters">
                      <button
                        type="button"
                        className={serviceCategoryFilter === 'all' ? 'active' : ''}
                        onClick={() => setServiceCategoryFilter('all')}
                      >
                        All <span>{serviceCategoryOptions.reduce((sum, option) => sum + option.count, 0)}</span>
                      </button>
                      {serviceCategoryOptions.map(option => (
                        <button
                          type="button"
                          key={option.value}
                          className={serviceCategoryFilter === option.value ? 'active' : ''}
                          onClick={() => setServiceCategoryFilter(option.value)}
                        >
                          {option.label} <span>{option.count}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {showServiceNavigation && selectedServices.length > 0 ? (
                <div className="booking-service-selection portal-service-selection">
                  <span>{selectedServices.length} selected</span>
                  <strong>{selectedServices.map(service => service.name).join(' + ')}</strong>
                </div>
              ) : null}
              {activeServiceSection ? (
                <div className={activeServiceSection.title ? 'portal-service-section' : ''}>
                  {activeServiceSection.title ? <div className="portal-service-section-title">{activeServiceSection.title}</div> : null}
                  {visibleServiceGroups.length ? visibleServiceGroups.map(([group, list]) => (
                    <div key={`${activeServiceSection.key}-${group || 'services'}`} className="portal-service-group">
                      {group ? <div className="portal-service-group-title">{group}</div> : null}
                      {list.map(service => {
                        const priceLabel = displayedServicePrice(service, { legacyFixed: !generalWorkshopEnabled })
                        const bookingLabel = service.booking_label || serviceBookingLabel(service)
                        return (
                          <label key={service.id} className="portal-service-option">
                            <input type="checkbox" checked={form.service_ids.includes(service.id)} onChange={() => toggleService(service.id)} />
                            <span>
                              <strong>{service.name}</strong>
                              <small>{[generalWorkshopEnabled ? serviceCategoryLabel(service) : service.category, service.brand].filter(Boolean).join(' - ')}</small>
                              {service.description ? <em>{service.description}</em> : null}
                              {bookingLabel ? (
                                <span className="portal-service-tags">
                                  <span className="portal-service-badge">{bookingLabel}</span>
                                </span>
                              ) : null}
                            </span>
                            <b>{priceLabel}</b>
                          </label>
                        )
                      })}
                    </div>
                  )) : (
                    <div className="booking-service-empty">No services match the current filter.</div>
                  )}
                </div>
              ) : null}
              {showServiceNavigation && normalisedServiceSearch ? <div className="booking-service-count">{visibleServiceCount} matching service{visibleServiceCount === 1 ? '' : 's'}</div> : null}
            </>
          ) : (
            <EmptyState title="No services published">Your workshop has not published a service menu yet. You can still send a general request using the notes box.</EmptyState>
          )}
          {generalWorkshopEnabled && servicePricing.summary_label ? (
            <div className="portal-service-total">
              <span>{servicePricing.pricing_mode === 'fixed' ? 'Estimated total' : 'Booking estimate'}</span>
              <strong>{servicePricing.pricing_mode === 'fixed' ? servicePricing.total_label : servicePricing.summary_label}</strong>
            </div>
          ) : null}
        </div>

        <div className="field">
          <label>Notes</label>
          <textarea
            value={form.notes}
            onChange={e => update('notes', e.target.value)}
            placeholder={selectedHasGeneralService ? 'Tell the workshop what bike, component, area, or problem this service relates to.' : 'Anything the workshop should know before confirming...'}
          />
        </div>

        <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Sending...' : 'Send Booking Request'}</button>
      </form>
    </>
  )
}

function ReportDetail({ data, token, reportId }) {
  const report = data.reports.find(item => item.id === reportId)
  const generalReport = isGeneralReport(report)
  const [signedUrl, setSignedUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    async function loadReport() {
      if (!reportId || !token) return
      setLoading(true)
      setError('')
      try {
        const res = await fetch(`/.netlify/functions/customer-portal-data?reportId=${encodeURIComponent(reportId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const payload = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(payload.error || 'Could not open report.')
        if (alive) setSignedUrl(payload.signed_url || '')
      } catch (err) {
        if (alive) setError(err.message || 'Could not open report.')
      } finally {
        if (alive) setLoading(false)
      }
    }
    loadReport()
    return () => { alive = false }
  }, [reportId, token])

  if (!report) return <div className="card"><EmptyState title="Report not found">This report is not available on your rider profile.</EmptyState></div>

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">{fmtDate(report.date)}</div>
          <div className="text-muted fs12 mt4">{report.service_summary}</div>
        </div>
        <Link className="btn btn-ghost" to="/portal/reports">Back to reports</Link>
      </div>
      <section className="card">
        {error ? <div className="alert alert-error">{error}</div> : null}
        <div className="portal-detail-grid">
          <div className="portal-detail"><span>Service</span><strong>{report.service_summary}</strong></div>
          <div className="portal-detail"><span>Bike</span><strong>{text(report.bike_name)}</strong></div>
          {!generalReport && <div className="portal-detail"><span>Component</span><strong>{text(report.component_name)}</strong></div>}
          {!generalReport && <div className="portal-detail"><span>Serial</span><strong>{text(report.component_serial)}</strong></div>}
          <div className="portal-detail"><span>Date</span><strong>{fmtDate(report.date)}</strong></div>
          {!generalReport && <div className="portal-detail"><span>Total</span><strong>{money(report.total_charged)}</strong></div>}
          {!generalReport && <div className="portal-detail"><span>Invoice</span><strong>{text(report.invoice_number)}</strong></div>}
          {!generalReport && <div className="portal-detail"><span>Next due</span><strong>{fmtDate(report.next_service_date)}</strong></div>}
          <div className="portal-detail"><span>Status</span><strong>{report.delivery_status}</strong></div>
        </div>
        <div className="page-actions" style={{ marginTop: 16, justifyContent: 'flex-start' }}>
          <a className="btn btn-primary" href={signedUrl || undefined} target="_blank" rel="noopener noreferrer" aria-disabled={!signedUrl}>
            {loading ? 'Preparing PDF...' : 'Open PDF Report'}
          </a>
        </div>
      </section>
    </>
  )
}

function ProfilePage({ data, token, onProfileSaved }) {
  const [form, setForm] = useState({ ...EMPTY_PROFILE, ...(data.profile || {}) })
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  useEffect(() => {
    setForm({ ...EMPTY_PROFILE, ...(data.profile || {}) })
  }, [data.profile])

  function updateField(field, value) {
    setForm(current => ({ ...current, [field]: value }))
  }

  async function saveProfile(event) {
    event.preventDefault()
    setSaving(true)
    setNotice('')
    setError('')
    try {
      const res = await fetch('/.netlify/functions/customer-portal-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ action: 'update_profile', profile: form }),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(payload.error || 'Could not save profile.')
      onProfileSaved(payload.profile)
      setNotice('Profile preferences saved.')
    } catch (err) {
      setError(err.message || 'Could not save profile.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-title">Profile / Preferences</div>
          <div className="text-muted fs12 mt4">These details help your workshop and future setup tools understand your riding context.</div>
        </div>
      </div>
      <form className="card" onSubmit={saveProfile}>
        {notice ? <div className="alert alert-success">{notice}</div> : null}
        {error ? <div className="alert alert-error">{error}</div> : null}
        <div className="grid-3">
          <div className="field">
            <label>Height (cm)</label>
            <input type="number" inputMode="decimal" value={form.height_cm || ''} onChange={e => updateField('height_cm', e.target.value)} />
          </div>
          <div className="field">
            <label>Body weight (kg)</label>
            <input type="number" inputMode="decimal" value={form.weight_kg || ''} onChange={e => updateField('weight_kg', e.target.value)} />
          </div>
          <div className="field">
            <label>Riding weight (kg)</label>
            <input type="number" inputMode="decimal" value={form.riding_weight_kg || ''} onChange={e => updateField('riding_weight_kg', e.target.value)} />
          </div>
        </div>
        <div className="grid-2">
          <div className="field">
            <label>Riding style</label>
            <input value={form.riding_style || ''} onChange={e => updateField('riding_style', e.target.value)} placeholder="Trail, enduro, bike park..." />
          </div>
          <div className="field">
            <label>Preferred feel</label>
            <input value={form.preferred_feel || ''} onChange={e => updateField('preferred_feel', e.target.value)} placeholder="Supportive, plush, poppy..." />
          </div>
        </div>
        <div className="field">
          <label>Notes / preferences</label>
          <textarea value={form.preferences || ''} onChange={e => updateField('preferences', e.target.value)} placeholder="Riding goals, comfort issues, terrain, race setup notes..." />
        </div>
        <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Preferences'}</button>
      </form>
    </>
  )
}

export default function RiderPortalPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [token, setToken] = useState('')
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loginEmail, setLoginEmail] = useState('')
  const [loginSent, setLoginSent] = useState(false)
  const [loginBusy, setLoginBusy] = useState(false)
  const [loginError, setLoginError] = useState('')

  useEffect(() => {
    let alive = true

    async function load() {
      setLoading(true)
      setError('')
      try {
        const url = new URL(window.location.href)
        const code = url.searchParams.get('code')
        if (code) {
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code)
          if (exchangeError) throw exchangeError
          clearPortalMagicIntent()
          window.history.replaceState({}, document.title, safePortalPath(window.location.pathname || '/portal'))
        }

        const { data: sessionData } = await supabase.auth.getSession()
        const session = sessionData?.session
        if (!session?.access_token) throw new Error('Use your workshop-issued access link to open your rider profile.')

        const res = await fetch('/.netlify/functions/customer-portal-data', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        })
        const payload = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(payload.error || 'No active rider portal access found.')
        if (!isValidPortalDataPayload(payload)) throw new Error('No active rider portal access found.')
        if (alive) {
          clearPortalMagicIntent()
          setToken(session.access_token)
          setData(payload)
        }
      } catch (err) {
        if (alive) setError(err.message || 'No active rider portal access found.')
      } finally {
        if (alive) setLoading(false)
      }
    }

    load()
    return () => { alive = false }
  }, [])

  async function signOut() {
    await supabase.auth.signOut()
    setData(null)
    setError('Signed out. Use your workshop-issued access link to sign back in.')
    navigate('/portal')
  }

  async function sendPortalLogin(event) {
    event.preventDefault()
    setLoginBusy(true)
    setLoginSent(false)
    setLoginError('')
    try {
      const portalPath = safePortalPath(`${location.pathname || '/portal'}${location.search || ''}`)
      rememberPortalMagicIntent(portalPath)
      if (isStagingTestMode()) throw new Error(stagingEmailMessage)
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: loginEmail,
        options: {
          emailRedirectTo: `${window.location.origin}${portalPath}`,
          shouldCreateUser: false,
        },
      })
      if (otpError) throw otpError
      setLoginSent(true)
    } catch (err) {
      clearPortalMagicIntent()
      setLoginError(err.message || 'Could not send portal login link.')
    } finally {
      setLoginBusy(false)
    }
  }

  const bikeComponents = useMemo(() => {
    const groups = {}
    ;(data?.components || []).forEach(component => {
      const key = component.bike_id || 'unlinked'
      if (!groups[key]) groups[key] = []
      groups[key].push(component)
    })
    return groups
  }, [data])

  const latestReport = data?.reports?.[0] || null

  function handleProfileSaved(profile) {
    setData(current => current ? { ...current, profile } : current)
  }

  function handleSetupSaved(setup) {
    setData(current => {
      if (!current) return current
      const existing = current.setup_records || []
      const without = existing
        .filter(record => record.id !== setup.id)
        .map(record => (
          setup.is_active && record.can_edit && record.component_id === setup.component_id
            ? { ...record, is_active: false }
            : record
        ))
      return { ...current, setup_records: [setup, ...without] }
    })
  }

  function handleRecommendationSaved(recommendation) {
    setData(current => {
      if (!current) return current
      const existing = current.recommendations || []
      const without = existing.filter(record => record.id !== recommendation.id)
      return { ...current, recommendations: [recommendation, ...without] }
    })
  }

  function handleBikeSaved(bike) {
    setData(current => {
      if (!current) return current
      const without = (current.bikes || []).filter(item => item.id !== bike.id)
      return { ...current, bikes: [...without, bike] }
    })
  }

  function handleComponentSaved(component) {
    setData(current => {
      if (!current) return current
      const without = (current.components || []).filter(item => item.id !== component.id)
      return { ...current, components: [...without, component] }
    })
  }

  let content = null
  if (data) {
    if (location.pathname === '/portal/profile') content = <ProfilePage data={data} token={token} onProfileSaved={handleProfileSaved} />
    else if (location.pathname === '/portal/bikes') content = <BikesList data={data} token={token} bikeComponents={bikeComponents} onBikeSaved={handleBikeSaved} />
    else if (location.pathname === '/portal/book') content = <PortalBookingPage data={data} token={token} />
    else if (location.pathname === '/portal/passport') content = <PassportPage token={token} />
    else if (location.pathname.startsWith('/portal/components/')) content = <ComponentDetailPage data={data} token={token} componentId={pathId(location.pathname, '/portal/components/')} onSetupSaved={handleSetupSaved} onRecommendationSaved={handleRecommendationSaved} />
    else if (location.pathname.startsWith('/portal/bikes/')) content = <BikeDetail data={data} token={token} bikeComponents={bikeComponents} bikeId={pathId(location.pathname, '/portal/bikes/')} onComponentSaved={handleComponentSaved} />
    else if (location.pathname === '/portal/reports') content = <ReportsList data={data} token={token} />
    else if (location.pathname.startsWith('/portal/reports/')) content = <ReportDetail data={data} token={token} reportId={pathId(location.pathname, '/portal/reports/')} />
    else content = <Dashboard data={data} bikeComponents={bikeComponents} latestReport={latestReport} />
  }

  if (loading) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ maxWidth: 680 }}>
          <div className="auth-logo">Rebound</div>
          <div className="empty" style={{ minHeight: 120 }}><span className="spinner" style={{ width: 20, height: 20 }} /></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ maxWidth: 680 }}>
          <div className="auth-logo">Rebound</div>
          <h1>Rider portal</h1>
          <div className="alert alert-error">{error}</div>
          <form onSubmit={sendPortalLogin} style={{ marginTop: 16 }}>
            <p className="text-muted">
              If your workshop has already enabled rider access, enter the invited email address and we will send a fresh secure login link.
            </p>
            {loginSent ? <div className="alert alert-success">Secure rider login link sent. Open it from your email to continue.</div> : null}
            {loginError ? <div className="alert alert-error">{loginError}</div> : null}
            <div className="field">
              <label>Email address</label>
              <input
                type="email"
                value={loginEmail}
                onChange={event => setLoginEmail(event.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            <button className="btn btn-primary" type="submit" disabled={loginBusy}>
              {loginBusy ? 'Sending...' : 'Send Rider Login Link'}
            </button>
          </form>
          <Link className="btn btn-ghost" to="/login">Workshop login</Link>
        </div>
      </div>
    )
  }

  return <PortalShell data={data} onSignOut={signOut}>{content}</PortalShell>
}
